import { Link, useLoaderData } from "@remix-run/react";
import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { getSupabaseWithSessionHeaders } from "../lib/supabase_server";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Contact,
  DollarSign,
  Handshake,
  Megaphone,
  Settings,
  ShoppingCart,
  Store,
  TrendingUp,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Header from "../components/header";
import Footer from "../components/Footer";
import Button from "../components/theme/Button";

export async function loader({ request }: LoaderFunctionArgs) {
  const { headers, supabase, session } = await getSupabaseWithSessionHeaders({
    request,
  });
  let userName = "";
  let isAdmin = false;
  if (session) {
    const { data } = await supabase
      .from("Users")
      .select("first_name, admin")
      .eq("id", session.user.id)
      .single();
    userName = data?.first_name ?? "";
    isAdmin = Boolean(data?.admin);
  }
  return json(
    { userId: session?.user?.id ?? "", userName, isAdmin },
    { headers },
  );
}

export const meta: MetaFunction = () => {
  return [
    { title: "Sell on BulkBuyPlace - Start Your Wholesale Storefront" },
    {
      name: "description",
      content:
        "Create your storefront, list products, and receive orders from local buyers. No upfront cost.",
    },
  ];
};

type TourBullet = string | { text: string; comingSoon?: boolean };

interface TourStep {
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  description: string;
  bullets: TourBullet[];
  image: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
}

const tour: TourStep[] = [
  {
    icon: Store,
    eyebrow: "Step 1 — Your storefront",
    title: "A storefront buyers can browse",
    description:
      "Set up your store in minutes with your logo, name and tagline, then list your products. Buyers get a clean page they can search and shop.",
    bullets: [
      "Banner with your logo, store name and tagline",
      "Home, Products and Reviews tabs plus in-store search",
      "Featured products with photos and your prices",
    ],
    image: {
      src: "/images/sell/storefront.jpeg",
      alt:
        "A seller's storefront page with a logo banner, store name and tagline, and a grid of featured products with prices.",
      width: 1015,
      height: 528,
    },
  },
  {
    icon: ShoppingCart,
    eyebrow: "Step 2 — Incoming orders",
    title: "Review every order before you commit",
    description:
      "New orders land in one place with the customer, item count, delivery and payment method, and your subtotal. Confirm what you can fulfill, or reject the order — nothing is accepted on your behalf.",
    bullets: [
      "A pending count so you know what still needs a decision",
      "Customer, items, pickup or delivery, and payment method on each order",
      "Confirm Selection or Reject All, order by order",
    ],
    image: {
      src: "/images/sell/incoming-orders.jpeg",
      alt:
        "The seller admin Incoming Orders page, showing pending order cards with the customer, delivery and payment method, subtotal, and confirm or reject buttons.",
      width: 1005,
      height: 518,
    },
  },
  {
    icon: Settings,
    eyebrow: "Step 3 — Manage your stores",
    title: "Run one store, or several, from one account",
    description:
      "Admin Settings lists every store you own along with your role, its status, tier and when it was last updated — and you can open another storefront whenever you're ready.",
    bullets: [
      "Role, status and tier for each store at a glance",
      "Sort by name, role or last modified",
      "Create a new store any time",
    ],
    image: {
      src: "/images/sell/admin-stores.jpeg",
      alt:
        "The Admin Settings Stores page, showing a table of the user's stores with role, status and tier, plus a Create New Store button.",
      width: 1014,
      height: 514,
    },
  },
  {
    icon: Contact,
    eyebrow: "Step 4 — Customer relationships",
    title: "Know your buyers and stay in touch",
    description:
      "Every order builds a customer list. See who's buying, how often, and what they spend — then reach out when you have news or promos.",
    bullets: [
      "Customer roster — Names, emails, and phone numbers from every order, searchable in one place",
      "Spend & activity at a glance — Total customers, new buyers, average lifetime value, and your top repeat customer for any period",
      {
        text:
          "Send promotionals — Email or message your list when you're running a sale, new stock, or seasonal offer",
        comingSoon: true,
      },
      "Order history per customer — See order count, last order date, and total spent before you follow up",
    ],
    image: {
      src: "/images/sell/customer_management.png",
      alt:
        "The seller admin Customers page with metric cards for total customers, new customers, average lifetime value, and favorite customer, plus a searchable table of buyers with order counts, last order dates, and total spend.",
      width: 1015,
      height: 590,
    },
  },
];

const valueProps = [
  {
    icon: DollarSign,
    title: "No upfront cost",
    description:
      "List your products and start selling without paying anything to get started, scale as you grow.",
  },
  {
    icon: Users,
    title: "Reach new customers",
    description:
      "BulkBuyPlace actively markets the marketplace to retailers, restaurants, and businesses looking for international food suppliers.",
  },
  {
    icon: Handshake,
    title: "No middleman",
    description: "You manage pricing, fulfillment, and payment directly.",
  },
  {
    icon: TrendingUp,
    title: "Grow your business",
    description:
      "Expand your reach with a storefront today — and soon, ads managed in the same place, without separate tools.",
  },
];

const adChannels = [
  {
    name: "Facebook",
    src: "/images/sell/ads/facebook.svg",
  },
  {
    name: "Instagram",
    src: "/images/sell/ads/instagram.svg",
  },
  {
    name: "Google",
    src: "/images/sell/ads/google.svg",
  },
  {
    name: "TikTok",
    src: "/images/sell/ads/tiktok.svg",
  },
  {
    name: "Reddit",
    src: "/images/sell/ads/reddit.svg",
  },
  {
    name: "YouTube",
    src: "/images/sell/ads/youtube.svg",
  },
];

function AdChannelTile({
  channel,
}: {
  channel: (typeof adChannels)[number];
}) {
  return (
    <div className="flex w-[132px] shrink-0 flex-col items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-4 py-5 shadow-sm">
      <img
        src={channel.src}
        alt=""
        width={36}
        height={36}
        className="h-9 w-9"
        loading="lazy"
        decoding="async"
      />
      <span className="text-sm font-medium text-gray-700">{channel.name}</span>
    </div>
  );
}

function AdsCommercialBoard() {
  const rowA = adChannels;
  const rowB = [...adChannels].reverse();

  return (
    <div
      className="overflow-hidden rounded-2xl bg-white p-1.5 shadow-xl ring-1 ring-black/10"
      aria-label="Ad channels: Facebook, Instagram, Google, TikTok, Reddit, and YouTube"
    >
      <div
        className="relative overflow-hidden rounded-xl bg-gray-50 py-6"
        style={{
          maskImage:
            "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex overflow-hidden">
            <div className="flex w-max gap-3 pr-3 animate-marquee-left motion-reduce:animate-none">
              {[...rowA, ...rowA].map((channel, i) => (
                <AdChannelTile
                  key={`a-${channel.name}-${i}`}
                  channel={channel}
                />
              ))}
            </div>
          </div>
          <div className="flex overflow-hidden">
            <div className="flex w-max gap-3 pr-3 animate-marquee-right motion-reduce:animate-none">
              {[...rowB, ...rowB].map((channel, i) => (
                <AdChannelTile
                  key={`b-${channel.name}-${i}`}
                  channel={channel}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SellPage() {
  const { userId, userName, isAdmin } = useLoaderData<typeof loader>();
  return (
    <div className="flex min-h-[100dvh] flex-col bg-gray-50">
      <Header userId={userId} userName={userName} isAdmin={isAdmin} />

      <main className="flex-1 pt-16">
        {/* Hero */}
        <section className="relative overflow-hidden bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-20 md:px-8 md:py-28">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight">
                Start selling to buyers near you — and beyond
              </h1>
              <p className="mt-5 text-lg text-gray-600 max-w-2xl mx-auto">
                Create your storefront, list your products, and reach more
                buyers. We actively promote the marketplace to retailers,
                restaurants, and businesses looking for international food
                suppliers — while you stay in control of your pricing, payments,
                and fulfillment.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button mode="primary" size="lg" asChild>
                  <Link to="/admin/newstore">Create Your Store</Link>
                </Button>
                <Button mode="secondary" size="lg" asChild>
                  <Link to="/suppliers">Browse Existing Stores</Link>
                </Button>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  No upfront cost
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  You set your prices
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Accept or reject any order
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Product Tour */}
        <section className="px-4 py-16 md:px-8 md:py-20">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900">
              How it works
            </h2>
            <p className="mt-3 text-center text-gray-500 max-w-lg mx-auto">
              Five steps from storefront to growth — here&apos;s what you
              actually get.
            </p>

            <div className="mt-14 space-y-20 md:mt-16 md:space-y-28">
              {tour.map((step, i) => {
                const imageOnLeft = i % 2 === 1;
                return (
                  <div
                    key={step.title}
                    className="grid items-center gap-8 md:grid-cols-12 md:gap-12 lg:gap-16"
                  >
                    <div
                      className={`md:col-span-5 ${
                        imageOnLeft ? "md:order-2" : ""
                      }`}
                    >
                      <span className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-sm font-semibold text-orange-700">
                        <step.icon className="h-4 w-4" />
                        {step.eyebrow}
                      </span>
                      <h3 className="mt-4 text-xl md:text-2xl font-bold text-gray-900">
                        {step.title}
                      </h3>
                      <p className="mt-3 text-gray-600">{step.description}</p>
                      <ul className="mt-5 space-y-2.5">
                        {step.bullets.map((bullet) => {
                          const text = typeof bullet === "string"
                            ? bullet
                            : bullet.text;
                          const comingSoon = typeof bullet === "string"
                            ? false
                            : bullet.comingSoon;
                          return (
                            <li
                              key={text}
                              className="flex items-start gap-2.5"
                            >
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                              <span className="text-sm text-gray-600">
                                {text}
                                {comingSoon
                                  ? (
                                    <span className="ml-2 inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Coming soon
                                    </span>
                                  )
                                  : null}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    <div
                      className={`md:col-span-7 ${
                        imageOnLeft ? "md:order-1" : ""
                      }`}
                    >
                      <div className="overflow-hidden rounded-2xl bg-white p-1.5 shadow-xl ring-1 ring-black/10">
                        <img
                          src={step.image.src}
                          alt={step.image.alt}
                          width={step.image.width}
                          height={step.image.height}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-auto rounded-xl"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Step 5 — Ads */}
              <div className="grid items-center gap-8 md:grid-cols-12 md:gap-12 lg:gap-16">
                <div className="md:col-span-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-sm font-semibold text-orange-700">
                      <Megaphone className="h-4 w-4" />
                      Step 5 — Ads
                    </span>
                    <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Coming soon
                    </span>
                  </div>
                  <h3 className="mt-4 text-xl md:text-2xl font-bold text-gray-900">
                    Manage and run ads in one place
                  </h3>
                  <p className="mt-3 text-gray-600">
                    Place and track ads for your store across the channels
                    buyers already use — without leaving BulkBuyPlace.
                  </p>
                  <ul className="mt-5 space-y-2.5">
                    {[
                      "Facebook, Instagram, Google, TikTok, Reddit, and YouTube",
                      "Run campaigns from the same account that runs your store",
                      "See what works without juggling separate ad tools",
                    ].map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2.5">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                        <span className="text-sm text-gray-600">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="md:col-span-7">
                  <AdsCommercialBoard />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Value Props */}
        <section className="px-4 py-16 md:px-8 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900">
              Why sell on BulkBuyPlace
            </h2>

            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {valueProps.map((prop) => (
                <div
                  key={prop.title}
                  className="flex gap-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-lg hover:border-gray-300 transition-all"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                    <prop.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {prop.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {prop.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust */}
        <section className="px-4 py-16 md:px-8 md:py-20">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900">
              You stay in control
            </h2>
            <div className="mt-10 space-y-4">
              {[
                "You set your own prices and product listings",
                "Payments are handled directly between you and the buyer using your preferred method (cash, Zelle, etc.)",
                "You choose delivery radius and pickup options",
                "You accept or reject each order",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-green-600" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-4 py-16 md:px-8 md:py-20 bg-[#282723]">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Ready to start selling?
            </h2>
            <p className="mt-3 text-gray-400">
              Join suppliers already reaching new buyers on BulkBuyPlace.
            </p>
            <div className="mt-8">
              <Button mode="primary" size="lg" asChild>
                <Link
                  to="/admin/newstore"
                  className="group inline-flex items-center gap-2"
                >
                  Create Your Store
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
